import { useAuth } from '../contexts/AuthContext';
import { ModulePermissions, UserModulePermissions } from '../types/user';
import { useCallback, useMemo } from 'react';
import { DEFAULT_PERMISSIONS, NO_PERMISSIONS, hasPermissionLevel } from '../config/permissions';

export const usePermissions = () => {
  const { user } = useAuth();

  // Effektív jogosultságok lekérése - memorizált verzió
  const permissions = useMemo((): UserModulePermissions => {
    if (!user) {
      return NO_PERMISSIONS;
    }

    // Super admin mindig teljes jogosultságokat kap, függetlenül az egyedi jogosultságoktól
    if (user.role === 'super_admin') {
      return DEFAULT_PERMISSIONS.super_admin;
    }

    // Ha nincs szervezet rendelve (kivéve super_admin), korlátozott jogosultságok
    if (!user.organizationId) {
      // Csak alapvető funkciók érhetők el (dashboard, profil)
      return {
        ...NO_PERMISSIONS,
        settings: 'access' // Profil beállítások elérhetők
      };
    }

    // Felhasználó egyedi jogosultságai vagy alapértelmezett szerepkör alapján
    const basePermissions = user.permissions || DEFAULT_PERMISSIONS[user.role] || DEFAULT_PERMISSIONS.user;
    
    // Biztonság: minden szükséges mező legyen benne az objektumban
    const finalPermissions: UserModulePermissions = {
      customers: basePermissions.customers || 'none',
      suppliers: basePermissions.suppliers || 'none',
      products: basePermissions.products || 'none',
      inventory: basePermissions.inventory || 'none',
      quotations: basePermissions.quotations || 'none',
      deals: basePermissions.deals || 'none',
      sales: basePermissions.sales || 'none',
      contracts: basePermissions.contracts || 'none',
      templates: basePermissions.templates || 'none',
      forms: basePermissions.forms || 'none',
      worksheets: basePermissions.worksheets || 'none',
      billing: basePermissions.billing || 'none',
      reports: basePermissions.reports || 'none',
      settings: basePermissions.settings || 'none',
      admin: basePermissions.admin || 'none',
      tasks: basePermissions.tasks || 'none',
      projects: basePermissions.projects || 'none',
      notes: basePermissions.notes || 'none',
      appointments: basePermissions.appointments || 'none',
      customerPortal: basePermissions.customerPortal || 'none',
      fleetManagement: basePermissions.fleetManagement || 'none'
    };
    
    return finalPermissions;
  }, [user?.id, user?.role, user?.organizationId, user?.permissions]);

  const hasAccess = useCallback((module: keyof UserModulePermissions): boolean => {
    // Super admin minden modulhoz hozzáfér
    if (user?.role === 'super_admin') return true;
    
    const userLevel = permissions[module];
    const hasPermission = userLevel !== 'none';
    
    return hasPermission;
  }, [permissions, user?.role]);

  // Egyszerűsített jogosultság ellenőrzés egy adott modulhoz
  const hasPermission = useCallback((module: keyof UserModulePermissions, requiredLevel: ModulePermissions): boolean => {
    // Super admin minden jogosultsággal rendelkezik
    if (user?.role === 'super_admin') return true;
    
    const userLevel = permissions[module];
    
    // Központi jogosultság szint ellenőrzés használata
    return hasPermissionLevel(userLevel, requiredLevel);
  }, [permissions, user?.role]);

  // Alapvető hozzáférés ellenőrzés
  const canAccess = useCallback((module: keyof UserModulePermissions): boolean => {
    return hasPermission(module, 'access');
  }, [hasPermission]);

  // Admin jogosultság ellenőrzés
  const canAdmin = useCallback((module: keyof UserModulePermissions): boolean => {
    return hasPermission(module, 'admin');
  }, [hasPermission]);

  // Backward compatibility függvények - DEPRECATED
  // Ezek a függvények már nem ajánlottak, használd a canAccess és canAdmin függvényeket
  const canRead = useCallback((module: keyof UserModulePermissions): boolean => {
    return canAccess(module);
  }, [canAccess]);

  const canWrite = useCallback((module: keyof UserModulePermissions): boolean => {
    // A write jogosultság legalább access szintet igényel
    return canAccess(module);
  }, [canAccess]);

  // Szervezeti hozzáférés ellenőrzés
  const canAccessOrganization = useCallback((organizationId: string): boolean => {
    if (!user) return false;
    if (user.role === 'super_admin') return true;
    return user.organizationId === organizationId;
  }, [user?.role, user?.organizationId]);

  // Navigációs elemek szűrése jogosultságok alapján
  const getAvailableModules = useCallback((): (keyof UserModulePermissions)[] => {
    // Super admin minden modulhoz hozzáfér
    if (user?.role === 'super_admin') {
      return Object.keys(permissions) as (keyof UserModulePermissions)[];
    }
    
    return Object.keys(permissions).filter(module => 
      permissions[module as keyof UserModulePermissions] !== 'none'
    ) as (keyof UserModulePermissions)[];
  }, [permissions, user?.role]);

  // További szükséges értékek memorizálása
  const isAdmin = useMemo(() => user?.role === 'admin' || user?.role === 'super_admin', [user?.role]);
  const isSuperAdmin = useMemo(() => user?.role === 'super_admin', [user?.role]);

  return {
    permissions,
    hasAccess,
    hasPermission,
    canAccess,
    canRead,
    canWrite,
    canAdmin,
    canAccessOrganization,
    getAvailableModules,
    isAdmin,
    isSuperAdmin
  };
}; 